// docs/47 — how every spell looks: an element (its colours) and a form (how it travels and lands),
// per hero ability and per foe's cast. Pure data and timing; `game/spells/SpellFx.ts` draws it.
// The playback plan gives each spell its own clip (plan.ts): gathered in the caster's hands for
// `WINDUP_MS` (the director starts the travel sound there too, fk_sfx::score::SPELL_WINDUP_MS),
// then it flies or falls, lands at the clip's impact, and the entries it caused play after it.
import type { Tile } from '../sim/types';
import { abilityOf, levelLook } from '../sim/progression';
import { abilityItem, abilitySet, abilityUpgrade, catalogueItem, fightUse, type CatalogueItem } from '../items/catalogue';

export type Element =
  | 'arcane' | 'force' | 'fire' | 'frost' | 'lightning' | 'thunder' | 'acid' | 'holy' | 'radiant'
  | 'shadow' | 'nature' | 'poison' | 'life' | 'smoke' | 'steel' | 'wild'
  // docs/104: the druid's earth and wind, the bard's music
  | 'earth' | 'wind' | 'music';

export type Form =
  | 'bolt'    // a missile from the caster to the aim, trailing sparks
  | 'darts'   // three homing darts on curving paths, one after another
  | 'ball'    // a lobbed ball that bursts over the area
  | 'beam'    // a line from the caster through its squares, all at once (lightning, a frost ray)
  | 'nova'    // a ring thrown out from the caster
  | 'column'  // comes down from the sky on the aim
  | 'storm'   // many things falling across the area
  | 'wall'    // flames rising along the squares one after another
  | 'bloom'   // the ground itself answers: vines, thorns, roots
  | 'aura'    // settles over the target: a shimmer, a ring of motes
  | 'curse'   // a sigil closing on the target
  | 'blink'   // gone in a twist of light, back somewhere else
  | 'cloud'   // a billow of smoke
  | 'touch'   // a hand's reach: the spell grips the adjacent target
  | 'volley'  // arrows arcing up and raining on the area
  // docs/104
  | 'smite'   // a bright blade comes down over the target in an arc (the paladin's Judgement)
  | 'sow'     // a seed of the land arcs to the aim and the ground answers (the druid's shaping)
  | 'gust'    // streaks of wind and leaves along the line (Gale)
  | 'song'    // musical notes rise from the bard and drift to the friends or foes it plays to
  | 'jeer'    // one sharp note thrown like a dart (Vicious Mockery)
  | 'finale'; // notes spiral up round the bard and burst out in every direction

export interface Palette { core: number; glow: number; edge: number; dark: number }

/** Colours by element: a white-hot core, the element's glow, its edge, and a dark for smoke and soot. */
export const PALETTES: Record<Element, Palette> = {
  arcane: { core: 0xffffff, glow: 0xe0b8ff, edge: 0x9b5cff, dark: 0x2a1446 },
  force: { core: 0xffffff, glow: 0xc9d7ff, edge: 0x6f8cff, dark: 0x1c2450 },
  fire: { core: 0xfff3b0, glow: 0xffb347, edge: 0xff5a1f, dark: 0x3a2a24 },
  frost: { core: 0xffffff, glow: 0xbfefff, edge: 0x5cc8ff, dark: 0x1d3a52 },
  lightning: { core: 0xffffff, glow: 0xfff7a8, edge: 0x7fd7ff, dark: 0x1c2a4a },
  thunder: { core: 0xffffff, glow: 0xd8e6ff, edge: 0x8aa8ff, dark: 0x262c44 },
  acid: { core: 0xf4ffb0, glow: 0xb6f24a, edge: 0x4caf1f, dark: 0x1f3a12 },
  holy: { core: 0xffffff, glow: 0xfff1a8, edge: 0xffc84a, dark: 0x4a3a12 },
  radiant: { core: 0xffffff, glow: 0xffe28a, edge: 0xff9f2a, dark: 0x4a2a10 },
  shadow: { core: 0xe8d0ff, glow: 0x9a5ae0, edge: 0x5a2a9a, dark: 0x140a22 },
  nature: { core: 0xeaffc8, glow: 0x9be06a, edge: 0x3f8a2a, dark: 0x3a2a14 },
  poison: { core: 0xeaffd0, glow: 0xa6e05a, edge: 0x6a3a9a, dark: 0x1e2a12 },
  life: { core: 0xf2fff0, glow: 0x9ff5b0, edge: 0x3fbf6a, dark: 0x123a22 },
  smoke: { core: 0xe4e4ea, glow: 0xa8acb6, edge: 0x5a5e68, dark: 0x22252c },
  steel: { core: 0xffffff, glow: 0xd8e8ff, edge: 0x7aa7ff, dark: 0x1c2a44 },
  wild: { core: 0xffffff, glow: 0xffd88a, edge: 0xa0d860, dark: 0x2a2a14 },
  earth: { core: 0xf4e6c8, glow: 0xd2a868, edge: 0x8a6034, dark: 0x3a2814 },
  wind: { core: 0xffffff, glow: 0xe2f4ee, edge: 0x92c4b8, dark: 0x2a3a38 },
  music: { core: 0xffffff, glow: 0xffe9a0, edge: 0xf0b030, dark: 0x4a3410 },
};

/**
 * docs/104 BD4: a bard's notes take the colour of their instrument (calling ids 61–65): the lute's
 * gold, the war drum's crimson, the flute's green, the harp's silver-blue, the horn's bronze.
 */
export const INSTRUMENT_PALETTES: Readonly<Record<number, Palette>> = {
  61: PALETTES.music,
  62: { core: 0xffe6dc, glow: 0xff8a6a, edge: 0xd0342a, dark: 0x3a0e0a },
  63: { core: 0xf0fff2, glow: 0x9cf0b8, edge: 0x2fb874, dark: 0x0e3a24 },
  64: { core: 0xffffff, glow: 0xd2e8ff, edge: 0x7aa8ec, dark: 0x1a2a4a },
  65: { core: 0xfff0d8, glow: 0xffc27a, edge: 0xd47a2a, dark: 0x3a200a },
};
/** The instrument an instrument's own song names (Ballad of Valour 190 → the lute 61 … the horn 65). */
export const instrumentOfSong = (ability: number | undefined): number | undefined => (ability !== undefined && ability >= 190 && ability <= 194 ? ability - 129 : undefined);
/** The colours a cast is drawn in: the bard's instrument for music, else its element's. */
export const castPalette = (c: Pick<SpellCast, 'look' | 'instrument' | 'ability'>): Palette => {
  if (c.look.element !== 'music') return PALETTES[c.look.element];
  const inst = instrumentOfSong(c.ability) ?? c.instrument;
  return (inst !== undefined ? INSTRUMENT_PALETTES[inst] : undefined) ?? PALETTES.music;
};

export interface SpellLook {
  name: string;
  element: Element;
  form: Form;
  /** How hard the board shakes when it lands (0 none, 1 a thump, 2 a great boom). */
  shake?: 0 | 1 | 2;
  /** How many darts, bolts or falling things. */
  count?: number;
  /** A second element mixed in (Flame Strike's holy fire, Ice Storm's thunder). */
  accent?: Element;
  /** What it leaves behind for a moment: embers, frost on the ground, a lingering glow. */
  linger?: 'embers' | 'frost' | 'sparks' | 'motes' | 'smoke' | 'puddle' | 'leaves';
}

const L = (name: string, element: Element, form: Form, more: Partial<SpellLook> = {}): SpellLook => ({ name, element, form, ...more });

/** Every hero ability drawn as a spell, by ability id (fk-grammar; docs/47 SP2). */
export const HERO_SPELLS: Readonly<Record<number, SpellLook>> = {
  // Fighter: a soldier's tricks are not magic, but their moments still shine
  11: L('Guard', 'steel', 'aura'),
  13: L('Hold the Line', 'steel', 'aura'),
  15: L('Second Wind', 'life', 'aura', { linger: 'motes' }),
  // Rogue
  21: L('Hide', 'smoke', 'cloud'),
  24: L('Smoke', 'smoke', 'cloud', { linger: 'smoke' }),
  // Wizard
  30: L('Spark', 'arcane', 'bolt', { linger: 'sparks' }),
  31: L('Arcane Ward', 'arcane', 'aura'),
  32: L('Fireball', 'fire', 'ball', { shake: 2, linger: 'embers' }),
  33: L('Slow', 'frost', 'curse'),
  34: L('Blink', 'arcane', 'blink'),
  35: L('Frost Line', 'frost', 'beam', { linger: 'frost' }),
  36: L('Wall of Fire', 'fire', 'wall', { shake: 1, linger: 'embers' }),
  37: L('Magic Missile', 'force', 'darts', { count: 3 }),
  38: L('Thunderwave', 'thunder', 'nova', { shake: 2 }),
  39: L('Lightning Bolt', 'lightning', 'beam', { shake: 1, linger: 'sparks', accent: 'thunder' }),
  70: L('Ray of Frost', 'frost', 'beam', { linger: 'frost' }),
  71: L('Hold Person', 'arcane', 'curse', { accent: 'force' }),
  72: L('Acid Splash', 'acid', 'ball', { shake: 1, linger: 'puddle' }),
  73: L('Ice Storm', 'frost', 'storm', { shake: 2, count: 18, linger: 'frost', accent: 'thunder' }),
  // Cleric
  41: L('Heal', 'life', 'aura', { linger: 'motes' }),
  42: L('Bless', 'holy', 'aura', { linger: 'motes' }),
  43: L('Sanctuary', 'holy', 'aura'),
  44: L('Mend', 'life', 'nova', { linger: 'motes' }),
  45: L('Rebuke', 'radiant', 'bolt', { shake: 1 }),
  46: L('Revive', 'holy', 'column', { linger: 'motes' }),
  47: L('Sacred Flame', 'radiant', 'column', { shake: 1, linger: 'motes' }),
  48: L('Guiding Bolt', 'holy', 'bolt', { shake: 1, linger: 'motes' }),
  49: L('Spirit Guardians', 'holy', 'nova', { count: 3, linger: 'motes' }),
  80: L('Healing Word', 'life', 'aura', { linger: 'motes' }),
  81: L('Bane', 'shadow', 'curse', { count: 4 }),
  82: L('Flame Strike', 'radiant', 'column', { shake: 2, accent: 'fire', linger: 'embers' }),
  83: L('Inflict Wounds', 'shadow', 'touch', { shake: 1 }),
  // Ranger
  51: L('Mark', 'wild', 'curse'),
  52: L('Snare', 'nature', 'bloom'),
  56: L('Rain of Arrows', 'steel', 'volley', { count: 14, shake: 1 }),
  57: L('Hail of Thorns', 'nature', 'ball', { linger: 'leaves' }),
  58: L('Entangle', 'nature', 'bloom', { linger: 'leaves' }),
  59: L('Lightning Arrow', 'lightning', 'bolt', { shake: 1, linger: 'sparks', accent: 'thunder' }),
  // docs/59: the Dragonborn's breath, a fighter's feat, the patrons' prayers
  100: L('Dragon Breath', 'fire', 'beam', { shake: 1, linger: 'embers' }),
  113: L('Battle Cry', 'steel', 'nova'),
  130: L('Dawnlight', 'radiant', 'aura', { linger: 'motes' }),
  132: L('Grasping Roots', 'nature', 'bloom', { linger: 'leaves' }),
  133: L('Veil', 'shadow', 'aura'),
  134: L("Ferryman's Toll", 'shadow', 'bolt', { accent: 'frost' }),
  135: L('Thunderclap', 'thunder', 'nova', { shake: 2 }),
  // the wizard's level-1 page
  140: L('Burning Hands', 'fire', 'nova', { linger: 'embers' }),
  141: L('Shocking Grasp', 'lightning', 'touch', { linger: 'sparks' }),
  142: L('Sleep', 'arcane', 'cloud', { linger: 'motes' }),
  143: L('Grease', 'acid', 'ball', { linger: 'puddle' }),
  144: L('Chill Touch', 'shadow', 'bolt', { accent: 'frost' }),
  145: L('Mage Armor', 'force', 'aura'),
  146: L('Misty Step', 'smoke', 'blink'),
  147: L('Fog Cloud', 'smoke', 'cloud', { linger: 'smoke' }),
  148: L('Color Spray', 'wild', 'beam', { linger: 'sparks' }),
  // docs/104: the paladin — radiance and steel
  200: L('Judgement', 'radiant', 'smite', { shake: 1, accent: 'steel', linger: 'motes' }),
  201: L('Interpose', 'holy', 'aura'),
  203: L('Lay on Hands', 'life', 'aura', { accent: 'holy', linger: 'motes' }),
  204: L('Shield of Faith', 'holy', 'aura', { linger: 'motes' }),
  206: L('Consecrate', 'radiant', 'nova', { shake: 1, accent: 'holy', linger: 'motes' }),
  // the druid — the land itself, its wind and its weather
  210: L('Thornwhip', 'nature', 'bolt', { linger: 'leaves' }),
  211: L('Overgrowth', 'nature', 'sow', { linger: 'leaves' }),
  212: L('Barkskin', 'nature', 'aura', { accent: 'earth' }),
  214: L('Quagmire', 'earth', 'sow', { linger: 'puddle' }),
  215: L('Wither', 'earth', 'sow', { accent: 'nature', linger: 'leaves' }),
  216: L('Call Lightning', 'lightning', 'column', { shake: 2, accent: 'thunder', linger: 'sparks' }),
  217: L('Wall of Thorns', 'nature', 'bloom', { shake: 1, linger: 'leaves' }),
  184: L('Verdant Bloom', 'life', 'nova', { accent: 'nature', linger: 'leaves' }),
  185: L('Briar Burst', 'nature', 'nova', { shake: 1, linger: 'leaves' }),
  186: L('Upheaval', 'earth', 'sow', { shake: 2 }),
  187: L('Gale', 'wind', 'gust', { linger: 'leaves' }),
  // the bard — music: notes rise from the bard and drift where the song goes
  220: L('Vicious Mockery', 'shadow', 'jeer'),
  221: L('Inspire', 'music', 'song', { count: 6 }),
  223: L('Finale', 'music', 'finale', { shake: 1, count: 26 }),
  224: L('Dissonant Whispers', 'shadow', 'song', { count: 7 }),
  225: L('Song of Rest', 'music', 'song', { count: 9, linger: 'motes' }),
  226: L('Encore', 'music', 'song', { count: 6 }),
  227: L('Countercharm', 'music', 'song', { count: 9, accent: 'arcane' }),
  190: L('Ballad of Valour', 'music', 'song', { count: 9 }),
  191: L('War Drum', 'music', 'song', { count: 8, shake: 1 }),
  192: L("Piper's Lure", 'music', 'song', { count: 6 }),
  193: L('Hymn of Mending', 'music', 'song', { count: 9, linger: 'motes' }),
  194: L('Rallying Horn', 'music', 'song', { count: 10 }),
};

/** docs/104 BD3: the songs (`AbilityId::is_song`), which add 1 Tempo when played. */
export const SONGS: ReadonlySet<number> = new Set([220, 221, 224, 225, 227, 190, 191, 192, 193, 194]);

/** docs/72 IT18: the form an item's ability takes when its `fx` names none, by its area and effects
 * (AUTHORING.md: blast → ball, line → beam, self → aura, single → bolt or touch). */
function defaultForm(it: CatalogueItem): Form {
  const a = it.active ?? fightUse(it);
  const area = a?.area ?? 'single';
  if (!a) return 'aura';
  if (a.effects.some((e) => 'teleport' in e)) return 'blink';
  if (area === 'self' || a.range === 'self') return 'aura';
  if (area === 'adjacent') return 'nova';
  if (typeof area === 'object' && 'blast' in area) return a.effects.some((e) => 'damage' in e) ? 'ball' : 'cloud';
  if (typeof area === 'object' && 'line' in area) return 'beam';
  if (typeof area === 'object' && 'allies_within' in area) return 'nova';
  if (a.effects.some((e) => 'heal' in e || 'revive' in e || 'cleanse' in e)) return 'aura';
  if (a.range === 'reach' || a.range === 1) return 'touch';
  if (a.effects.some((e) => 'damage' in e)) return 'bolt';
  return 'curse';
}

/** How big a moment an item's ability is: a legendary bomb shakes the board, a potion does not. */
function itemShake(it: CatalogueItem, form: Form): 0 | 1 | 2 {
  if (form === 'aura' || form === 'blink' || form === 'cloud' || form === 'curse') return 0;
  return it.rarity >= 4 ? 2 : it.rarity >= 2 || form === 'ball' || form === 'storm' || form === 'column' ? 1 : 0;
}

/** docs/72 IT18: a catalogue piece's look, from its `fx` (element, form, accent, linger). */
export function itemLook(it: CatalogueItem): SpellLook | undefined {
  const a = it.active ?? fightUse(it);
  if (!a || !it.fx) return undefined;
  const form = it.fx.form ?? defaultForm(it);
  const more: Partial<SpellLook> = { shake: itemShake(it, form) };
  if (it.fx.accent) more.accent = it.fx.accent;
  if (it.fx.linger) more.linger = it.fx.linger;
  if (form === 'darts') more.count = it.rarity >= 3 ? 4 : 3;
  if (form === 'storm') more.count = 8 + 4 * it.rarity;
  if (form === 'volley') more.count = 6 + 3 * it.rarity;
  return L(a.name, it.fx.element, form, more);
}

/** docs/119: what a level ability's element leaves behind for a moment. */
const LINGER: Partial<Record<string, SpellLook['linger']>> = {
  fire: 'embers', frost: 'frost', lightning: 'sparks', holy: 'motes', radiant: 'motes', life: 'motes', smoke: 'smoke', acid: 'puddle', nature: 'leaves', earth: 'leaves', poison: 'puddle',
};

/** Every ability's look: a hero's spell (HERO_SPELLS) or an item's (`10000 + def`). */
export function lookFor(abilityId: number): SpellLook | undefined {
  const hero = HERO_SPELLS[abilityId];
  if (hero) return hero;
  // docs/119: an ability levelling brings carries its look in the progression catalogue (weapon
  // work has no form and is drawn as blows)
  if (abilityId >= 300 && abilityId <= 599) {
    const l = levelLook(abilityId);
    const a = l?.form ? abilityOf(abilityId) : undefined;
    if (l?.form && a) {
      const big = a.effects.some((e) => typeof e === 'object' && e !== null && 'Damage' in e) && a.resource_cost >= 3;
      return L(a.name, l.element as Element, l.form as Form, {
        ...(big ? { shake: 2 as const } : l.form === 'nova' || l.form === 'column' || l.form === 'storm' ? { shake: 1 as const } : {}),
        ...(l.form === 'storm' ? { count: 16 } : l.form === 'darts' ? { count: 3 } : l.form === 'volley' ? { count: 12 } : l.form === 'song' ? { count: 8 } : {}),
        ...(LINGER[l.element] ? { linger: LINGER[l.element] } : {}),
      });
    }
    return undefined;
  }
  const it = abilityItem(abilityId);
  if (it) return itemLook(it);
  // docs/112: an upgrade's ability looks as its fx says, at its upgrade's rarity
  const u = abilityUpgrade(abilityId);
  const ua = u?.up.active?.[u.rank - 1];
  if (u && ua && u.up.fx) {
    return itemLook({ id: 0, name: u.up.name, family: 'charm', rarity: Math.min(4, u.up.rarity + 1) as CatalogueItem['rarity'], icon: '', flavour: '', source: '', tags: [], active: ua, fx: u.up.fx });
  }
  // docs/110: a set's ability looks as its bonus says, as big a moment as a legendary's
  const set = abilitySet(abilityId);
  const first = set && catalogueItem(set.members[0]!);
  return set && first ? itemLook({ ...first, rarity: 4, active: set.bonus.active, fx: set.bonus.fx, use: undefined }) : undefined;
}

/** The look of a piece used from the bag (an `ItemUsed` with no cast of its own). */
export const itemDefLook = (def: number): SpellLook | undefined => { const it = catalogueItem(def); return it ? itemLook(it) : undefined; };

/** A foe's spell's element by its people (mirrors fk_sfx::score `monster_element`). */
export function foeElement(kin: string | undefined): Element {
  switch (kin) {
    case 'Spider': case 'Ooze': case 'Lizardfolk': case 'SpiderQueen': case 'Basilisk': return 'poison';
    case 'Goblin': case 'Orc': case 'Troll': case 'Dragon': return 'fire';
    case 'Gnoll': case 'Wolf': case 'Beast': case 'MountainTroll': case 'Sandworm': case 'GreatEagle': case 'Ent': return 'nature';
    case 'Yeti': case 'GrimGiant': case 'WinterCourt': return 'frost';
    // docs/109
    case 'Demon': case 'Djinn': case 'Drake': case 'Elemental': case 'Duergar': return 'fire';
    case 'Gorgon': case 'Vermin': case 'Mimic': return 'poison';
    case 'Fey': case 'Minotaur': case 'Cyclops': return 'nature';
    case 'Golem': case 'Beholder': return 'arcane';
    case 'Soldier': return 'holy';
    // docs/117
    case 'Tentacles': case 'SandTentacles': return 'nature';
    case 'BlackDragon': return 'shadow';
    // docs/178: the kraken's ink and the black deep; the maw's drool and bite
    case 'Kraken': return 'shadow';
    case 'MawOfTheDeep': return 'poison';
    default: return 'shadow';
  }
}

export type FoeCastKind = 'bolt' | 'burst' | 'curse' | 'mend' | 'ward';

/** How a foe's cast looks: its people's element thrown as a bolt or burst, a curse, a mend. */
export function foeLook(kind: FoeCastKind, kin: string | undefined): SpellLook {
  const el = foeElement(kin);
  switch (kind) {
    case 'bolt': return L('Foe bolt', el, 'bolt', { linger: el === 'fire' ? 'embers' : undefined });
    case 'burst': return L('Foe burst', el, 'ball', { shake: 1, linger: el === 'fire' ? 'embers' : el === 'poison' ? 'puddle' : undefined });
    case 'curse': return L('Foe curse', 'shadow', 'curse', { count: 3 });
    case 'mend': return L('Foe mend', 'life', 'aura', { linger: 'motes' });
    case 'ward': return L('Foe ward', 'steel', 'aura');
  }
}

/** One spell as the playback draws it (the plan's cast clip carries it). */
export interface SpellCast {
  look: SpellLook;
  caster: number;
  from: Tile;
  /** Where it flies or falls: the target's square, the blast's heart, the line's first square. */
  aim?: Tile;
  /** Every square it covers (the blast, the line, the ring). */
  tiles: Tile[];
  /** Units its entries hurt, heal or touch, in log order (the darts' targets, the aura's friends). */
  targets: number[];
  /** Squares of those units as the pulse began. */
  targetTiles: Tile[];
  ability?: number;
  /** docs/72 IT19: the catalogue piece behind an item's cast (its `sfx.use` plays on the clip). */
  itemDef?: number;
  /** docs/104 BD4: the bard's instrument (calling id 61–65) when the scene could read it: its notes take its colour. */
  instrument?: number;
}

/** How long the spell is gathered in the caster's hands (ms at 1×; fk_sfx `SPELL_WINDUP_MS`, so it
 * holds under reduce motion too: the director's travel sound starts there). */
export const WINDUP_MS = 380;

/** Manhattan distance in squares. */
const span = (a: Tile, b: Tile | undefined) => (b ? Math.abs(a.x - b.x) + Math.abs(a.y - b.y) : 0);

/** The flight and the landing of a spell, in ms at 1× (windup excluded). */
export function spellTiming(c: SpellCast): { travelMs: number; holdMs: number } {
  const d = span(c.from, c.aim ?? c.targetTiles[0]);
  switch (c.look.form) {
    case 'bolt': return { travelMs: 140 + 45 * d, holdMs: 220 };
    case 'darts': return { travelMs: 260 + 40 * d + 130 * ((c.look.count ?? 3) - 1), holdMs: 160 };
    case 'ball': return { travelMs: 220 + 55 * d, holdMs: 420 };
    case 'beam': return { travelMs: 120, holdMs: 380 };
    case 'nova': return { travelMs: 60, holdMs: 460 };
    case 'column': return { travelMs: 260, holdMs: 420 };
    case 'storm': return { travelMs: 420, holdMs: 520 };
    case 'wall': return { travelMs: 120 + 90 * Math.max(1, c.tiles.length), holdMs: 360 };
    case 'bloom': return { travelMs: 160, holdMs: 420 };
    case 'aura': return { travelMs: c.targetTiles.some((t) => span(c.from, t) > 0) ? 160 + 30 * d : 60, holdMs: 340 };
    case 'curse': return { travelMs: 120 + 30 * d, holdMs: 380 };
    case 'blink': return { travelMs: 120, holdMs: 260 };
    case 'cloud': return { travelMs: 80, holdMs: 420 };
    case 'touch': return { travelMs: 120, holdMs: 300 };
    case 'volley': return { travelMs: 520 + 30 * d, holdMs: 300 };
    case 'smite': return { travelMs: 300, holdMs: 360 };
    case 'sow': return { travelMs: 220 + 45 * d, holdMs: 320 };
    case 'gust': return { travelMs: 260, holdMs: 360 };
    case 'song': return { travelMs: 420 + 30 * d, holdMs: 360 };
    case 'jeer': return { travelMs: 120 + 35 * d, holdMs: 260 };
    case 'finale': return { travelMs: 480, holdMs: 560 };
  }
}

/** The cast clip's length and impact (ms at 1×): windup + travel, then the landing holds. */
export function spellClipMs(c: SpellCast, reduceMotion = false): { ms: number; impactMs: number } {
  const t = spellTiming(c);
  const travel = reduceMotion ? Math.min(t.travelMs, 160) : t.travelMs;
  const hold = reduceMotion ? Math.min(t.holdMs, 200) : t.holdMs;
  return { ms: WINDUP_MS + travel + hold, impactMs: WINDUP_MS + travel };
}
